/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslocoModule } from '@jsverse/transloco';
import { isMongoId } from 'class-validator';
import { Observable, Subject, catchError, debounceTime, filter, finalize, first, map, merge, of, switchMap, takeUntil, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { Option } from '@hmax-order/shared/types';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { HttpClientService } from '../../http-client/http-client.service';
import { ValidateFieldsService } from '../validate-fields.service';

@Component({
  selector: 'hmax-input-find-register',
  standalone: true,
  templateUrl: './input-find-register.component.html',
  styleUrls: ['./input-find-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CapitalizePipe,
    CommonModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
})
export class InputFindRegisterComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;

  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() iconRegistered = 'feather:user-check';
  @Input() iconNoRegister = 'feather:user-plus';
  @Input() iconSize = 5;
  @Input() resource!: string;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() autocomplete = '';
  @Input() selectedOption = true; // Definir que o usuário deve escolher uma opção da lista
  @Input() filterInput!: string;
  @Input() filter: { [key: string]: any } = {};
  @Input() addRoute!: string;
  @Input() inputRegister!: string;
  /** Nome do campo com o Id temporário do cadastro. Default "tempId" */
  @Input() tempIdFieldName = 'tempId';
  /** Origem do cadastro com o qual precisa ser vinculado */
  @Input() origin = '';
  @Input() onBlur = false;
  /** Utilizado para focar no primeiro elemento do formulário*/
  @Input() id!: string;
  /** Busca os dados ao focar no input, sem precisar digitar */
  @Input() fetchOnFocus = false;

  @Output() blurEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() loadingEmitter: EventEmitter<any> = new EventEmitter<boolean>();

  options$?: Observable<Option[]>;
  private _manualTrigger$ = new Subject<string>();
  options!: Option[];
  loading = false;
  isRegister!: string;

  get formControl(): AbstractControl {
    return this.formGroup?.controls[this.controlName];
  }

  get findIcon(): string {
    return this.isRegister ? this.iconRegistered : this.iconNoRegister;
  }

  get findTooltip(): string {
    return this.isRegister ? 'open-register' : 'no-register';
  }

  public _http = inject(HttpClientService);
  public validateField = inject(ValidateFieldsService);

  ngOnInit(): void {
    // Carregar controlName com o label se não houver controlName definido
    this.controlName = this.controlName ?? this.label;
    this.loadOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.verifyTempId();

    if (changes['options']) {
      this.loadOptions();
    }
  }

  ngAfterViewInit(): void {
    // Limpar a opção informada se não existir na lista de opções para ser selecionado
    if (this.selectedOption) {
      this.trigger?.panelClosingActions.subscribe((e) => {
        if (!(e && e?.source) && !this.existValueInOptions()) {
          this.formControl?.setValue('');
          this.trigger.closePanel();
        }
      });
    }

    this.verifyIsRegister();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Retornar opções HTTP com parâmetros se um filtro estiver presente
   */
  private getHttpOptions(): any {
    if (!this.filter) {
      return undefined;
    }

    const params: HttpParams = this._http.getParams(this.filter);
    return { params: params };
  }

  /**
   * Definir o valor do filtro com base no valor de entrada fornecido
   * @param value Valor do input (string ou objeto)
   */
  private setFilter(input: any): void {
    const isString = typeof input === 'string';

    if (this.filterInput) {
      const value = isString ? input : input?.name;
      if (this.filterInput != 'id' && !isMongoId(value)) {
        if (value) {
          this.filter[this.filterInput] = value;
        } else {
          delete this.filter[this.filterInput];
        }
      }
    }

    if (this.inputRegister) {
      const valueRegister = isString ? input : input?.[this.inputRegister];

      // Só adiciona se NÃO for id OU (for id E for um MongoID válido)
      const shouldSet = this.inputRegister === 'id' && isMongoId(valueRegister);

      if (shouldSet) {
        this.filter[this.inputRegister] = valueRegister;
      }
    }
  }

  /**
   * Dispara busca ao focar no input, sem precisar digitar (requer fetchOnFocus=true)
   */
  onInputFocus(): void {
    if (!this.fetchOnFocus) return;
    // Dispara MESMO com um registro já selecionado: clicar no campo precisa abrir a lista para
    // permitir trocar a escolha. Antes havia um `return` quando já existia id, e o resultado era
    // um campo que só mostrava opções depois de o usuário digitar.
    this._manualTrigger$.next(this.formControl?.value ?? '');
  }

  /**
   * Carregar opções do select com as opções informadas
   */
  private loadOptions(): void {
    // Digitação tem debounce (evita uma requisição por tecla); o foco NÃO — a lista precisa
    // abrir assim que o usuário clica. Antes o debounce era comum aos dois e o clique só
    // respondia depois de 400ms.
    const typed$ = (this.formControl?.valueChanges ?? of(null)).pipe(
      filter((value) => this.checkOptionFilterValue(value)),
      debounceTime(400),
      map((value) => ({ value, fromFocus: false })),
    );
    const focused$ = this._manualTrigger$.pipe(map((value) => ({ value, fromFocus: true })));

    this.options$ = merge(typed$, focused$).pipe(
      takeUntil(this._unsubscribeAll),
      switchMap(({ value, fromFocus }) => this.handleValueChange(value, fromFocus)),
      tap(() => this.verifyIsRegister()),
      catchError((error) => {
        throw error;
      }),
    );
  }

  /**
   * Verificar se o valor esta apto para pesquisa
   * @param value Valor do campo
   */
  private checkOptionFilterValue(value: any): boolean {
    const isString = typeof value === 'string';
    const inputValue = isString ? value : value?.name;

    return inputValue?.length >= 2;
  }

  /**
   * Verificar se o campo esta valido com um registro existente no banco
   * @param input Valor no input
   */
  private verifyIsRegister(): void {
    const input = this.formControl?.value;
    if (this.inputRegister && input?.value) {
      this.isRegister = input?.value[this.inputRegister];
    } else {
      this.isRegister = '';
      // Guardas de `?.` porque nada garante que o formulário do consumidor declare o control:
      // sem elas, um `controlName` que não existe no `FormGroup` derruba a tela inteira.
      if (this.inputRegister) this.formControl?.get(this.inputRegister)?.setValue(null);
    }
  }

  /**
   * Manipular o evento de alteração de valor definindo filtros e buscando opções atualizadas
   * @param value Valor do input
   */
  private handleValueChange(value: any, fromFocus = false): Observable<Option[]> {
    if (fromFocus && this.filterInput) {
      // Foco lista TUDO: filtrar pelo que já está selecionado devolveria só a própria opção,
      // e o usuário não conseguiria trocar de registro sem apagar o campo antes.
      delete this.filter[this.filterInput];
    } else {
      this.setFilter(value);
    }

    return this.fetchUpdatedOptions(value, fromFocus);
  }

  /**
   * Aplicar filtros a opções e atualiza opções com novos valores
   */
  private applyOptionFilter(options: Option[], value: any): Option[] {
    this.options = options;
    this.verifySelectedOption();

    if (!this.filterInput) {
      const name = typeof value === 'string' ? value : value?.name;
      return name ? this.filterOptions(name as string) : options;
    } else {
      return options;
    }
  }

  /**
   * Verificar opção carregada com as opções recuperadas do back-end
   */
  private verifySelectedOption(): void {
    const fieldValue = this.formControl?.value;

    const option = this.findOptionByFieldValue(fieldValue);

    if (option) {
      this.formControl.setValue(option, { emitEvent: false });
    } else if (fieldValue && typeof fieldValue === 'string') {
      this.formControl?.setValue({ name: fieldValue, value: { name: fieldValue } }, { emitEvent: false });
    }
  }

  /**
   * Encontrar uma opção na lista com base no valor do campo fornecido
   * @param fieldValue O valor a ser pesquisado nas opções
   */
  private findOptionByFieldValue(fieldValue: any): Option | undefined {
    return this.options?.find((option: Option) =>
      this.inputRegister ? option?.value?.[this.inputRegister] === fieldValue : option?.name === fieldValue,
    );
  }

  /**
   * Filtrar opções a medida que digitamos no select
   * @param value Valor do input para ser filtrado
   */
  private filterOptions(value: string): Option[] {
    const valueLower = value.toLowerCase();

    return this.options?.filter((option: Option) => {
      return option?.name?.toLowerCase().includes(valueLower) || option?.value === value;
    });
  }

  /**
   * Verificar se a opção já seleciona existe na lista de opções
   */
  private existValueInOptions(): boolean {
    const valueInInput = this.formControl.value?.value || this.formControl.value;

    const exist = this.options?.findIndex((option: Option) => {
      const valueOption = option?.value as any;

      return option?.value === valueInInput || (valueOption?.id === valueInInput?.id && valueOption?.id != null);
    });

    return exist !== -1;
  }

  /**
   * Buscar opções atualizadas com base nos filtros informados
   */
  private fetchUpdatedOptions(value: any, fromFocus = false): Observable<Option[]> {
    const options: any = this.getHttpOptions();

    const option = this.formControl?.value;
    const hasId = option?.value?.[this.inputRegister] ?? undefined;

    // `hasId` evita refazer a busca a cada mudança quando já há registro escolhido — mas o foco
    // precisa furar essa guarda, senão clicar num campo preenchido devolve só a própria opção.
    if (this.resource && (fromFocus || !hasId)) {
      this.loading = true;
      this.loadingEmitter.emit(this.loading);
      return this._http.get(this.resource, options).pipe(
        first(),
        map((items: any[]) => {
          return items.map((item) => {
            return {
              name: item?.name ?? item?.label ?? '',
              value: item,
            } as Option;
          });
        }),
        map((updatedOptions: Option[]) => this.applyOptionFilter(updatedOptions, value)),
        finalize(() => {
          this.loading = false;
          this.loadingEmitter.emit(this.loading);
        }),
      );
    }

    return of(hasId ? [option] : []);
  }

  /**
   * Gerar url com dados para um novo cadastro de pessoa
   */
  private getUrlNewUser(): string {
    this.verifyTempId();
    const tempId = this.formGroup?.controls[this.tempIdFieldName]?.value;
    const personName = this.formGroup?.controls['name']?.value?.name;

    let queryParams = `origin=${this.origin}`;

    if (tempId) {
      queryParams += `&tempId=${tempId}`;
    }

    if (personName) {
      queryParams += `&personName=${personName}`;
    }

    queryParams += '&routeAfterSave=close';

    return `${this.addRoute}/new?${queryParams}`;
  }

  /**
   * Control do id temporário, quando o formulário do consumidor declara um.
   *
   * 🔴 O id temporário serve ao fluxo de "cadastrar na hora" (`addRoute`), e **só o
   * `company-detail` declara esse control** (`personTempId`). Todo outro consumidor — o campo de
   * grupo do produto, por exemplo — usa o componente sem ele, então aqui o control **é ausente por
   * construção**, não por esquecimento: ausente significa "este formulário não usa id temporário".
   */
  private get tempIdControl(): AbstractControl | undefined {
    return this.formGroup?.controls[this.tempIdFieldName];
  }

  /**
   * Gerar um Id temporário
   */
  private generateTempId(): void {
    const tempId = uuidv4();

    this.tempIdControl?.setValue(tempId);
  }

  /**
   * Verificar se deve remover o ID temporário
   */
  private verifyTempId(): void {
    const tempIdControl = this.tempIdControl;
    // Sem control de id temporário não há nada a sincronizar. A linha de limpeza acessava o
    // control sem guarda e quebrava a tela de produto: `[filter]` é um literal no template, então
    // `ngOnChanges` roda a cada change detection, e bastava o campo trazer um registro resolvido
    // (`isRegister` verdadeiro — produto COM grupo, como os do seed de empresa) para o dialog
    // morrer com `Cannot read properties of undefined (reading 'setValue')`.
    if (!tempIdControl) return;

    if (this.isRegister) {
      tempIdControl.setValue(null);
    } else if (!tempIdControl.value) {
      this.generateTempId();
    }
  }

  /**
   * Retornar a formação de como de como deve ser exibido no
   * campo input para o usuário.
   * @param Options Opções
   * @returns Como deve aparecer no campo, quando um item form selecionado
   */
  displaySelect(option: Option): string {
    return option?.name ? option?.name : '';
  }

  /**
   * Abrir uma nova aba de acordo com a validação do input
   */
  openNewTab(): void {
    if (!this.addRoute) {
      return;
    }

    if (!this.isRegister) {
      const url: string = this.getUrlNewUser();
      window.open(url, '_blank');
      return;
    }

    window.open(`${this.addRoute}/${this.isRegister}`, '_blank');
  }

  /**
   * Verificar se deve emitir valor de onBlur
   */
  checkOnBlur(): void {
    if (this.onBlur) {
      this.blurEmitter.emit(this.formControl.value);
    }
  }
}
